// WHERE a Work-board was filled in — the "context" it belongs to.
//
// Pouria, 2026-08-18: "when we change pages go home and … we clear work board and selected items,
// some how we need to keep them in one ongoing, when we go out should be removed." So the board is
// no longer global-forever: it is stamped with the context it was filled in and dropped the moment
// the user leaves that context.
//
// A context is a STRING so it can be compared with `!==` and persisted as-is:
//
//   `ongoing:<id>`  — a story and everything that hangs off it. Packs, the pack doc, compare and
//                     the email overlay all ride on `/ongoing/:id` as QUERY params, so they share
//                     the story's context and navigating between them keeps the board (which is the
//                     whole point: you tick things in the pack to save them into the story).
//   `home-research` — the home research session: what a (possibly signed-out) user ticked out of
//                     search results before any ongoing owns it.
//   `module:<prefix>` — the listing modules (/emails, /tasks, …). Moving inside one module keeps
//                     what was ticked there; leaving it does not.
//   `compose:<timelineId>` — the reply composer opened over a thread by the drafting gesture
//                     (busy-ai-srv#13). See `composeContext` below for why it is its own context.
//   `page:<path>`   — anything else, one context per page.
//
// Kept as a plain function (no store, no router) so the rule is testable on its own.
import { MODULE_PREFIXES, isHomePath, ongoingIdFromPath } from '@/lib/searchMode'
import { wantsReplyCompose } from '@/lib/draftEvidence'
import { isCreationSession } from '@/lib/creationCapabilities'
import { focusedModule } from '@/lib/focusedPage'

export const HOME_RESEARCH = 'home-research'
// The email session (busy-my-web#99). It is not a page — "Emails need attention" turns the middle
// into the Email Workspace wherever you are — so it cannot be derived from the path; the session
// store hands it to the board when it opens, and hands the PAGE's context back when it closes.
// That is what makes the ticked groups leave with the session rather than survive it.
export const EMAIL_SESSION = 'email-session'

/**
 * The reply composer as a context of its own (busy-ai-srv#13).
 *
 * `/emails/timeline/:id?compose=reply` is a thread inside the /emails module, so by the module rule
 * it would be `module:/emails` — and a draft's sources, handed over from the story that wrote them,
 * would then survive a walk back to the inbox. The composer is a SURFACE the user was taken to for
 * one job; naming it after the thread it is composing on gives the hand-off (see the board's
 * `handOff`) an exact target and makes "leaving the composer empties the board" true again.
 */
export function composeContext(timelineId) {
  const id = String(timelineId || '').trim()
  return id ? `compose:${id}` : null
}

// `/emails/timeline/:id` — the thread the composer is open on, or null on any other path.
const timelineIdFromPath = (p) => (String(p || '').match(/^\/emails\/timeline\/([^/?#]+)/) || [])[1] || null

export function workboardContext(path, { emailSession = false, query = null } = {}) {
  const p0 = String(path || '')
  // Most specific first: the composer was opened deliberately, by the reply gesture, and what is on
  // the board there belongs to the draft rather than to the page (or session) underneath it.
  if (wantsReplyCompose(query)) {
    const composing = composeContext(timelineIdFromPath(p0))
    if (composing) return composing
  }
  if (emailSession) return EMAIL_SESSION
  const p = p0
  const ongoingId = ongoingIdFromPath(p)
  if (ongoingId) return `ongoing:${ongoingId}`
  if (isHomePath(p)) return HOME_RESEARCH
  const prefix = MODULE_PREFIXES.find(m => p === m || p.startsWith(`${m}/`))
  if (prefix) return `module:${prefix}`
  return `page:${p}`
}

// Does arriving in `next` mean the board must be dropped? Only when the board actually has an
// owner (an empty board owns nothing) and the new context is a different one. An unknown/empty
// next context never clears — a navigation we cannot place is not evidence the user left.
export function leavesContext(owner, next) {
  if (!owner || !next) return false
  return owner !== next
}

/**
 * What arriving in `next` does to a board — the whole of ENTER_CONTEXT's decision, as a function.
 *
 * #84's rule is that a board is dropped on the way out of the context it was filled in, and that
 * stands for ordinary navigation. A reply draft is not ordinary navigation: the feature itself takes
 * the user from the story to the composer and puts the draft's sources on the board on the way, so
 * the rows would be leaving their owner in the very act of being written. A HAND-OFF is how the
 * feature says so — "these rows are expected in `compose:<id>`" — and the board honours it once, for
 * that one context: the arriving context becomes their owner, and leaving it empties the board
 * exactly as #84 says.
 *
 * A hand-off that is never taken up (the user goes somewhere else instead) changes nothing: the
 * normal rule applies and the pending target is dropped with the navigation that ignored it.
 *
 * @param owner   the context the board belongs to
 * @param handOff the context a hand-off is pending for, or null
 * @param next    the context just arrived in
 * @returns {{ clear: boolean, owner: string|null, handOff: null }} — clear the board? and its owner after
 */
export function enterContextEffect({ owner = null, handOff = null, next = null } = {}) {
  if (handOff && next && handOff === next) return { clear: false, owner: next, handOff: null }
  if (leavesContext(owner, next)) return { clear: true, owner: null, handOff: null }
  return { clear: false, owner: owner || null, handOff: null }
}

/**
 * …and the same question for what is TYPED in the universal input box (busy-my-web#226).
 *
 * Saffet: "I have text in the input box from the last ongoing in home screen". A half-written
 * question belongs to the thing it was written about just as much as the ticked items and the
 * Briefing's subject do — those already leave with their context (#84 for the board, #191/#122 for
 * the strip), and the ask was the one part of the session still riding along, arriving on home (and
 * in the NEXT ongoing) as text about a story the user had left.
 *
 * Same `leavesContext` rule, with one thing on top of it:
 *
 * `composing` — the box is not asking anything, it is holding a note/task/event/email/contact body
 *   or an entity opened for editing. That text is a DRAFT the user is writing, not a question about
 *   the page, and the creation flows clear it themselves when they finish or are cancelled. Wiping
 *   it because a navigation happened underneath (the creation notice's View routes while a form can
 *   still be open) would throw away work nobody asked to throw away.
 *
 * @param owner the context the text was typed in
 * @param next  the context just arrived in
 * @param opts  { composing } — see above
 * @returns {boolean} true when the ask must be emptied
 */
export function askLeavesContext(owner, next, { composing = false } = {}) {
  if (composing) return false
  return leavesContext(owner, next)
}

// ── The open Research Pack as the CREATION's context, not a chip on its board (busy-my-web#602) ──
//
// Before: an opened pack rides on the board as context (Pouria, 2026-08-17: "put opened resarach
// pack to work board and keep it until user remove it from work board" — see the workboard store's
// `isPack`), and that chip was there in every layout, a creation card's included. Starting a task
// inside an open pack therefore opened its Work-board already holding "<pack name> ×", which read as
// the thing tying the new item to the pack (the Briefing's pack row, the entity rail's "Created
// from", the timeline filing).
//
// Reversed for creation (busy-my-web#602 — Saffet, 2026-10-08: "as soon as you open a task or even
// a note the Work-Board should be empty so you can gather any document into the Work-Board and add
// it to the task"; Pouria's go, 2026-10-09): while a task / note / event / contact / call is being
// MADE, the board does not draw the open pack — it starts at its empty state, a place for the
// documents that will be attached. The link to the pack never came from the chip: it is the open
// pack itself (`?pack=` on the story route), which is the creation's context — `creationPackId`
// below is the one place the save reads it. Everywhere else (reading the pack, a search, an entity
// open for editing, an email being written) the chip stays exactly as 2026-08-17 asked. A pack the
// user TICKS (the comparison picks) is a selection, not this context chip, and is still drawn.

/** The creation modules an open pack files its new item into (lib/moduleColors' module keys). */
export const PACK_FILED_MODULES = Object.freeze(['task', 'note', 'event', 'contact', 'messaging'])

/**
 * Does the board draw the OPEN packs (the context chips) for this focused session?
 * @param session the `focusedSession/session` shape — { quickMode, formMode, formMenu, editing, replying }
 * @returns {boolean} false only while a pack-filed item is being created (busy-my-web#602)
 */
export function boardShowsOpenPacks(session) {
  const s = session || {}
  if (!isCreationSession(s) || s.editing || s.replying) return true
  return !PACK_FILED_MODULES.includes(focusedModule(s))
}

/**
 * The pack a new item is filed into — read off the creation's CONTEXT, never off the board.
 *
 * New notes/tasks/events attach to the ONGOING itself (Figma Stage 7) — never to a research pack —
 * except when the user is literally inside a focused pack page (`?pack=…`); then the store's
 * current pack is the one (busy-my-web#602 moved this out of SmartBox's `advanceOngoing`, unchanged).
 *
 * @param query         the route query
 * @param currentPackId `research/currentPackId`
 * @returns {string|null}
 */
export function creationPackId(query, currentPackId) {
  if (!(query && query.pack)) return null
  return currentPackId || null
}
